<template>
  <RailShell
    :class="{ sessionDim: dimmed }"
    :eyebrow="sortMode === 'sales_play' && !isDialerOnly ? 'Contacts by sales play' : 'Contacts'"
  >
    <template v-if="sessionControls || queuedCount > 1" #lead>
      <div v-if="sessionControls" class="railLead sessionLead">
        <div class="sessionRow">
          <span class="liveDot"></span>
          <span class="liveLabel">Live session</span>
          <span class="countText">{{ sessionPosition }} of {{ sessionTotal }}</span>
        </div>
        <div class="progTrack"><div class="progFill" :style="{ '--pct': sessionPct + '%' }"></div></div>
      </div>
      <div v-else class="railLead idleLead">
        <div class="powerWrap">
          <button
            type="button"
            class="btn primary powerBtn"
            :disabled="!cta.ready"
            :aria-label="cta.label"
            :title="cta.title"
            @click="$emit('start-dialing')"
          >
            <AppIcon name="play" :size="15" />
            <span class="powerLabel">{{ cta.label }}</span>
          </button>
          <!-- The avatar rail hides the checkboxes and the select bar, so the count keeps
               the scope visible and clearing it is the one way out of a picked set there. -->
          <button
            v-if="selectedCount"
            type="button"
            class="selBadge"
            aria-label="Clear selection"
            title="Clear selection"
            @click="$emit('clear-selection')"
          >{{ cta.ready }}</button>
        </div>
        <span class="railLeadSub">{{ cta.sub }}</span>
      </div>
    </template>

    <template #head-start>
      <!-- Master checkbox — same affordance as the Actions page toolbar. -->
      <input
        type="checkbox"
        class="masterCheckbox"
        :checked="allShownSelected"
        :indeterminate.prop="selectedCount > 0 && !allShownSelected"
        :aria-label="allShownSelected ? 'Deselect all' : 'Select all shown'"
        title="Select all shown"
        @change="$emit('toggle-page')"
      />
    </template>
    <template #head-end>
      <SortMenu
        v-if="queuedCount > 1"
        class="railSort"
        compact
        :model-value="sortMode"
        :options="sortOptions"
        :active="sortMode !== 'sales_play'"
        @update:model-value="(v: string) => $emit('update:sortMode', v as QueueSortMode)"
      />
      <FilterMenu class="railFilter" compact :active-count="activeFilterCount" @clear="$emit('clear-filters')">
        <!-- div, not label: a label re-dispatches clicks on non-interactive
             dropdown content (the "Searching…" row, list padding) to the
             trigger button, closing the dropdown mid-interaction. -->
        <!-- Hidden for dialer-only while every rep has one list (My Call List). -->
        <div v-if="!isDialerOnly" class="field">
          <span class="fieldLabel">{{ playLabel }}</span>
          <SearchSelect
            :modelValue="filterPlayId"
            :options="playOptions"
            :placeholder="`All ${playWordPlural}`"
            :search-placeholder="`Search ${playWordPlural}…`"
            :clear-label="`All ${playWordPlural}`"
            @update:modelValue="(v: string) => $emit('update:filterPlayId', v || '')"
          />
        </div>
        <div class="field">
          <span class="fieldLabel">Account</span>
          <SearchSelect
            :modelValue="filterAccountId"
            :optionsFunction="accountOptionsFn"
            :maxResults="20"
            placeholder="All accounts"
            search-placeholder="Search accounts…"
            clear-label="All accounts"
            @update:modelValue="(v: string) => $emit('update:filterAccountId', v || '')"
          />
        </div>
      </FilterMenu>
    </template>
    <!-- The host's own control (the page's collapse toggle). -->
    <template #head-aside>
      <slot name="head-aside" />
    </template>

    <!-- Bulk actions for the current selection. More actions land here. -->
    <template v-if="selectedCount" #bar>
      <div class="selectBar">
        <span class="count">{{ selectedCount }} selected</span>
        <button v-if="isDialerOnly" type="button" class="btn danger btn-sm" @click="$emit('bulk-remove')">Remove from call list</button>
        <button v-else type="button" class="btn danger btn-sm" @click="$emit('bulk-exit')">Exit play…</button>
      </div>
    </template>

    <!-- Nobody can be power dialed: say so, and point at the foot. -->
    <div v-if="canAddContacts && nothingReady" class="queueEmpty">
      <span>Nothing ready to dial.</span>
      <span class="queueEmptyHint">Add contacts below from your LeadIQ captures.</span>
    </div>

    <template v-for="s in sections" :key="s.key">
      <template v-if="s.total">
        <div v-if="s.title" class="sectionHead" :class="{ open: s.open }">
          <div class="sectionText">
            <span class="eyebrow">{{ s.title }}</span>
            <span class="sectionCount">{{ s.open ? s.total + ' being shown' : s.total + ' hidden' }}</span>
          </div>
          <button
            class="coldEye"
            type="button"
            role="switch"
            :aria-checked="s.open"
            :class="{ on: s.open }"
            :title="s.open ? SECTION_UI[s.key].hide : SECTION_UI[s.key].show"
            @click="$emit('toggle-section', s.key)"
          >
            <AppIcon :name="s.open ? 'eye' : 'eye_off'" :size="16" />
          </button>
        </div>

        <template v-if="s.open">
          <template v-for="g in s.groups" :key="s.key + ':' + g.salesPlayId">
            <!-- Ungrouped sorts render one synthetic group with no header. -->
            <RailGroupHead
              v-if="s.grouped"
              :color="g.color"
              :name="g.salesPlayName || 'Untagged'"
              :count="g.entries.length"
            >
              <button
                v-if="!isDialerOnly && g.salesPlayId && filterPlayId !== g.salesPlayId"
                type="button"
                class="gOnly"
                :title="`Show only ${g.salesPlayName || `this ${playWord}`}`"
                @click="$emit('update:filterPlayId', g.salesPlayId)"
              >Only</button>
            </RailGroupHead>
            <RailItem
              v-for="c in g.entries"
              :key="c.id"
              :data-entry-id="c.id"
              :name="c.contact_name || 'Unknown contact'"
              :sub="role(c)"
              :avatar-bg="SECTION_UI[s.key].avatarBg"
              :selected="c.id === selectedId"
              :muted="SECTION_UI[s.key].muted"
              :blocked="SECTION_UI[s.key].blocked"
              :meta-title="s.key === 'cooldown' ? callableAt(c) : undefined"
              @select="$emit('select', c.id)"
            >
              <template #lead>
                <input
                  type="checkbox"
                  class="rowCheckbox"
                  :checked="selectedIds.has(c.contact_id)"
                  :aria-label="`Select ${c.contact_name || 'contact'}`"
                  @click.stop="$emit('toggle-check', c.contact_id, $event)"
                  @keydown.stop
                />
              </template>
              <template #avatar>{{ initials(c.contact_name) }}</template>
              <template v-if="s.key === 'cooldown'" #meta><AppIcon name="wait" :size="11" />{{ callableAt(c) }}</template>
              <template v-else-if="s.key === 'noNumber'" #meta>{{ c.country ? countryName(c.country) : 'Can’t be dialed' }}</template>
            </RailItem>
          </template>

        </template>
      </template>
    </template>

    <div v-if="filteredEmpty" class="queueEmpty">
      <span>No contacts match these filters.</span>
      <span class="queueEmptyHint">{{ emptyHint }}</span>
      <button type="button" class="clearFiltersLink" @click="$emit('clear-filters')">
        Clear filters
      </button>
    </div>

    <!-- The foot: End session while a session runs; otherwise the way to bring people in. -->
    <template v-if="sessionControls || canAddContacts" #foot>
      <button
        v-if="sessionControls"
        type="button"
        class="btn danger-outline endSessionBtn"
        aria-label="End session"
        title="End the dial session"
        @click="$emit('end-session')"
      >
        <AppIcon name="stop" :size="15" />
        <span class="endLabel">End session</span>
      </button>
      <button
        v-else
        type="button"
        class="btn secondary addBtn"
        aria-label="Add contacts"
        title="Add contacts to your call list"
        @click="$emit('add-contacts')"
      >
        <AppIcon name="plus" :size="15" />
        <span class="addLabel">Add contacts</span>
      </button>
    </template>
  </RailShell>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import SearchSelect from '../../../components/SearchSelect.vue'
import FilterMenu from '../list/FilterMenu.vue'
import SortMenu from '../list/SortMenu.vue'
import RailShell from './rail/RailShell.vue'
import RailGroupHead from './rail/RailGroupHead.vue'
import RailItem from './rail/RailItem.vue'
import { countryName } from '../../utils/phone'
import { QUEUE_SORT_OPTIONS, type RailSection, type RailSectionKey } from '../../composables/useDialerQueue'
import type { CallerQueueEntry, QueueSortMode } from '../../../services/dialerApi'
import { useCurrentUser } from '../../../services/user'

const props = defineProps<{
  sections: RailSection[]
  selectedId: string | null
  selectedIds: ReadonlySet<string>
  selectedReadyCount?: number
  allShownSelected: boolean
  sortMode: QueueSortMode
  filterPlayId: string
  filterAccountId: string
  playOptions: { value: string; label: string }[]
  accountOptionsFn: (q: string) => Promise<{ value: string; label: string }[]>
  activeFilterCount: number
  queuedCount: number
  readyCount: number
  sessionControls?: boolean
  sessionPosition?: number
  sessionTotal?: number
  dimmed?: boolean
  canAddContacts?: boolean
  nothingReady?: boolean
}>()

defineEmits<{
  (e: 'start-dialing'): void
  (e: 'add-contacts'): void
  (e: 'clear-selection'): void
  (e: 'end-session'): void
  (e: 'select', id: string): void
  (e: 'toggle-check', id: string, ev: MouseEvent): void
  (e: 'toggle-page'): void
  (e: 'bulk-exit'): void
  (e: 'bulk-remove'): void
  (e: 'toggle-section', key: RailSectionKey): void
  (e: 'update:sortMode', value: QueueSortMode): void
  (e: 'update:filterPlayId', value: string): void
  (e: 'update:filterAccountId', value: string): void
  (e: 'clear-filters'): void
}>()

// Per-section presentation. Data (order, grouping, counts) lives in the
// composable's `sections`; how each section paints its rows lives here.
const SECTION_UI: Record<
  RailSectionKey,
  { muted: boolean; blocked: boolean; avatarBg: string; show: string; hide: string }
> = {
  warm: { muted: false, blocked: false, avatarBg: 'var(--hue-amber-soft)', show: '', hide: '' },
  cold: {
    muted: true,
    blocked: false,
    avatarBg: 'var(--hue-gray-soft)',
    show: 'Show cold contacts',
    hide: 'Hide cold contacts',
  },
  cooldown: {
    muted: true,
    blocked: false,
    avatarBg: 'var(--hue-gray-soft)',
    show: 'Show recently-called contacts',
    hide: 'Hide recently-called contacts',
  },
  noNumber: {
    muted: true,
    blocked: true,
    avatarBg: 'var(--hue-gray-soft)',
    show: 'Show contacts outside supported regions',
    hide: 'Hide contacts outside supported regions',
  },
}

const selectedCount = computed(() => props.selectedIds.size)
const dialable = (n: number) => `${n} can be power dialed`
// Any checked person scopes the power dial to the checked; the page runs what this counts.
const cta = computed(() => {
  if (selectedCount.value) {
    const ready = props.selectedReadyCount ?? 0
    return {
      label: 'Power Dial Selected',
      ready,
      title: ready ? 'Dial the selected contacts that are ready, top down' : 'None of the selected contacts can be called right now',
      sub: `${selectedCount.value} selected · ${dialable(ready)}`,
    }
  }
  const n = props.queuedCount
  return {
    label: 'Start Power Dialing',
    ready: props.readyCount,
    title: props.readyCount ? 'Dial every ready contact, top down' : 'No contacts are ready to dial',
    sub: `${n} ${n === 1 ? 'contact' : 'contacts'} in queue · ${dialable(props.readyCount)}`,
  }
})
const sessionPct = computed(() => {
  const total = props.sessionTotal ?? 0
  return total ? Math.round(((props.sessionPosition ?? 0) / total) * 100) : 0
})
const { isDialerOnly } = useCurrentUser()
// dialer-only reps have no sales-play product and
// never see that word — to them it is their dial list.
const playWord = computed(() => (isDialerOnly.value ? 'dial list' : 'sales play'))
const playWordPlural = computed(() => (isDialerOnly.value ? 'lists' : 'plays'))
const playLabel = computed(() => (isDialerOnly.value ? 'Call list' : 'Sales play'))
const sortOptions = computed(() =>
  QUEUE_SORT_OPTIONS.map((o) => (o.value === 'sales_play' ? { ...o, label: playLabel.value } : o)),
)

// "X being shown" in the top header = warm only (stable; the other sections
// carry their own sub-counts and never move this number — toggling them only
// changes the dialable "X can be power dialed" set, owned by the page).

const railTotal = computed(() => props.sections.reduce((n, s) => n + s.total, 0))
const filteredEmpty = computed(() => props.activeFilterCount > 0 && railTotal.value === 0)
// The account list is scoped to the selected play, so the pair can legitimately
// have no rows. Say which filter to drop rather than leaving a dead end.
const emptyHint = computed(() =>
  props.filterPlayId && props.filterAccountId
    ? `No contacts for this account in this ${playWord.value} — clear the account filter.`
    : 'Try clearing the filter.',
)

// "Callable ~HH:MM" from cooldown_until; falls back to a timeless label when the
// hide is due to an in-flight dispatch (no finished dial time yet).
function callableAt(c: CallerQueueEntry): string {
  if (!c.cooldown_until) return 'Called recently'
  const d = new Date(c.cooldown_until)
  if (Number.isNaN(d.getTime())) return 'Called recently'
  return 'Callable ~' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function initials(name: string | null): string {
  const n = String(name || '').trim()
  if (!n) return '?'
  const parts = n.split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?'
}
// Company first: when the line ellipsizes, a long title is the better thing to lose.
function role(c: CallerQueueEntry): string {
  return [c.account_name, c.contact_title].map((v) => String(v || '').trim()).filter(Boolean).join(' · ')
}
</script>

<style scoped>
.eyebrow {
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-tertiary);
}
/* Full-width icon+label: the cheapest target this layout allows, and it sits in
   the pane it acts on rather than above the contact detail. */
.railLead { display: flex; flex-direction: column; gap: var(--space-2); padding: var(--space-5) var(--space-4) 0; flex-shrink: 0; }
.powerWrap { position: relative; display: flex; width: 100%; }
.btn.powerBtn,
.btn.endSessionBtn,
.btn.addBtn { width: 100%; padding: var(--space-3) var(--space-4); }
.railLeadSub { text-align: center; font-size: var(--fs-xs); color: var(--color-text-tertiary); }
.selBadge { display: none; }
.sessionRow { display: flex; align-items: center; gap: var(--space-2); }
.liveDot { width: 8px; height: 8px; border-radius: 999px; background: var(--color-danger); animation: livePulse 1.2s var(--ease-out) infinite; flex-shrink: 0; }
@keyframes livePulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
.liveLabel { flex: 1; min-width: 0; font-size: var(--fs-sm); font-weight: var(--fw-semibold); color: var(--color-text-primary); }
.countText { font-size: var(--fs-xs); color: var(--color-text-tertiary); font-variant-numeric: tabular-nums; white-space: nowrap; }
.progTrack { height: 6px; border-radius: 999px; background: var(--color-bg-subtle); overflow: hidden; }
.progFill { height: 100%; width: var(--pct); border-radius: 999px; background: var(--color-accent); transition: width var(--duration-base) var(--ease-out); }

/* The session dim lives on the rail body, not the page wrapper, so the session
   lead, the host's head control and the foot (End session) stay at full strength. */
:deep(.queueHeadTop),
:deep(.queueScroll),
.selectBar,
.railLead { transition: opacity var(--duration-base) var(--ease-out); }
.sessionDim :deep(.queueHeadTop),
.sessionDim :deep(.queueScroll),
.sessionDim .selectBar,
.sessionDim .railLead.idleLead { opacity: 0.35; pointer-events: none; }

.selectBar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-4);
  border-top: 1px solid var(--color-border-subtle);
  border-bottom: 1px solid var(--color-border-subtle);
  background: var(--color-bg-subtle);
  flex-shrink: 0;
}
.count { flex: 1; font-size: var(--fs-sm); font-weight: var(--fw-semibold); color: var(--color-text-primary); white-space: nowrap; }

.sectionHead { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-4) var(--space-4) var(--space-3); margin-top: var(--space-3); border-top: 1px solid var(--color-border-subtle); }
.sectionText { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.sectionCount { font-size: var(--fs-sm); color: var(--color-text-secondary); }
.coldEye { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border: 0; border-radius: var(--radius-md); background: transparent; color: var(--color-text-tertiary); flex-shrink: 0; cursor: pointer; transition: color var(--duration-fast) var(--ease-out), background var(--duration-fast) var(--ease-out); }
.coldEye:hover { color: var(--color-text-secondary); background: var(--color-bg-subtle); }
.coldEye.on { color: var(--color-accent); background: var(--color-accent-soft); }

.queueEmpty {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-4) var(--space-4);
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
}
.queueEmptyHint { font-size: var(--fs-xs); opacity: 0.85; }
.clearFiltersLink {
  align-self: flex-start;
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-accent);
  cursor: pointer;
}
.clearFiltersLink:hover { text-decoration: underline; }

/* Revealed by hovering anywhere on the header row. */
.gOnly {
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 11px;
  font-weight: var(--fw-semibold);
  color: var(--color-text-tertiary);
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0;
}
.groupHead:hover .gOnly,
.gOnly:focus-visible { opacity: 1; }
.gOnly:hover { color: var(--color-text-primary); }

/* ── ADAPTIVE: a narrow expanded rail. Anchored right, the 260px panel plus the 48px of
   controls right of its trigger would clip at the dialer's left edge; open rightward instead. ── */
@container rail (min-width: 121px) and (max-width: 320px) {
  .railSort :deep(.menu),
  .railFilter :deep(.panel) { left: 0; right: auto; }
}

/* ── ADAPTIVE: the avatar rail (threshold defined in RailShell) ── */
@container rail (max-width: 120px) {
  .railLead { padding: var(--space-4) 0 0; align-items: center; }
  .powerWrap { width: 40px; }
  .btn.powerBtn,
  .btn.endSessionBtn,
  .btn.addBtn { width: 40px; height: 40px; padding: 0; }
  /* FilterMenu's count badge, inverted to read on the accent button; a sibling of the
     CTA, not a child, since a button cannot hold one. */
  .selBadge {
    display: block;
    position: absolute;
    top: -5px;
    right: -5px;
    min-width: 15px;
    height: 15px;
    padding: 0 3px;
    border: 0;
    border-radius: var(--radius-full);
    background: var(--color-bg-app);
    color: var(--color-accent);
    box-shadow: 0 0 0 1px var(--color-accent);
    font-family: inherit;
    font-size: 10px;
    font-weight: var(--fw-semibold);
    line-height: 15px;
    text-align: center;
    cursor: pointer;
  }
  .selBadge:hover { background: var(--color-accent); color: #fff; }
  /* HeroIcon writes its px size as an inline style; !important is the only override. */
  .btn.powerBtn :deep(svg),
  .btn.endSessionBtn :deep(svg),
  .btn.addBtn :deep(svg) { width: 18px !important; height: 18px !important; }
  .powerLabel, .railLeadSub, .endLabel, .addLabel, .liveLabel, .progTrack { display: none; }
  /* The collapsed session block is the dot and the count ("123 of 500" wraps). */
  .sessionRow { flex-direction: column; gap: var(--space-1); }
  .countText { white-space: normal; text-align: center; line-height: 1.2; }
  .sectionHead { justify-content: center; padding: var(--space-3) 0; }
  .sectionText { display: none; }
  .gOnly { display: none; }
  .queueEmpty { display: none; }
  /* No room for bulk selection or the sort menu in the avatar rail. The funnel
     stays — an ACTIVE filter must remain visible and clearable — with its panel
     flying out to the right, over the main content. */
  .masterCheckbox, .rowCheckbox, .selectBar { display: none; }
  .railSort { display: none; }
  .railFilter :deep(.panel) { left: calc(100% + 8px); right: auto; top: 0; }
}
</style>
